/* @jsx h */
import { FastNumberInput, LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { AnimatedMobileTipsScreen } from "./AnimatedMobileTipsScreen";
import {
  defineComponent,
  h,
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useNativeBridge, useAuth } from "../../providers";
import {
  DEFAULT_RECOMMENDED_TIP_SETTING,
  RestaurantSettingKey,
} from "@lemmonhq/types";
import type { RecommendedTipSetting } from "@lemmonhq/types";

interface TipsIntegratedPaymentModalProps {
  isOpen: boolean;
  paymentMethod: "card" | "twint" | null;
  totalAmount: number;
  initialTipAmount?: number;
  onClose: () => void;
  onConfirm: (tipAmount: number) => Promise<void>;
}

export const TipsIntegratedPaymentModal =
  defineComponent<TipsIntegratedPaymentModalProps>({
    name: "TipsIntegratedPaymentModal",
    props: [
      "isOpen",
      "paymentMethod",
      "totalAmount",
      "initialTipAmount",
      "onClose",
      "onConfirm",
    ],
    setup(props) {
      const { t } = useLocalization();
      const bridge = useNativeBridge();
      const auth = useAuth();
      const customTipAmount = ref<number | null>(null);
      const selectedTipAmount = ref(props.initialTipAmount || 0);
      const isSubmitting = ref(false);
      const isMobile = ref(
        typeof window !== "undefined" ? window.innerWidth < 600 : false,
      );

      const recommendedTips = computed<RecommendedTipSetting>(() => {
        const raw = auth.user.value?.restaurant_settings?.find(
          (s) => s.key === RestaurantSettingKey.RECOMMENDED_TIPS,
        );
        if (!raw?.value) return DEFAULT_RECOMMENDED_TIP_SETTING;
        try {
          return JSON.parse(raw.value) as RecommendedTipSetting;
        } catch {
          return DEFAULT_RECOMMENDED_TIP_SETTING;
        }
      });

      const baseAmount = computed(() => {
        const initialTip = props.initialTipAmount || 0;
        return Math.max(props.totalAmount - initialTip, 0);
      });

      watch(
        () => [props.isOpen, props.initialTipAmount],
        () => {
          if (!props.isOpen) {
            return;
          }

          const initialTip = props.initialTipAmount || 0;
          const { type, steps } = recommendedTips.value;
          const matchesQuickTip = steps.some((step) => {
            const tipAmount =
              type === "percentage"
                ? Number(((baseAmount.value * step) / 100).toFixed(2))
                : Number(step.toFixed(2));
            return Math.abs(initialTip - tipAmount) < 0.001;
          });

          selectedTipAmount.value = initialTip;
          customTipAmount.value =
            initialTip > 0 && !matchesQuickTip ? initialTip : null;
          isSubmitting.value = false;
        },
        { immediate: true },
      );

      const handleConfirm = async (tipAmount: number) => {
        if (isSubmitting.value) {
          return;
        }

        selectedTipAmount.value = tipAmount;
        isSubmitting.value = true;

        try {
          await props.onConfirm(tipAmount);
        } finally {
          isSubmitting.value = false;
        }
      };

      const handleQuickTip = (tipAmount: number) => {
        customTipAmount.value = null;
        void handleConfirm(tipAmount);
      };

      const handleCustomTipChange = (value: number | null) => {
        customTipAmount.value = value;

        if (value == null) {
          return;
        }

        void handleConfirm(value);
      };

      const updateViewport = () => {
        isMobile.value = window.innerWidth < 600;
      };

      onMounted(() => {
        updateViewport();
        window.addEventListener("resize", updateViewport);
      });

      onBeforeUnmount(() => {
        window.removeEventListener("resize", updateViewport);
      });

      const renderContent = () => (
        <div class="flex h-full flex-col bg-base-100 p-4">
          <div class="flex-1">
            <div class="rounded-2xl border  bg-base-200/40 p-4 text-center">
              <div class="text-sm uppercase tracking-wide ">Subtotal</div>
              <div class="mt-2 text-4xl font-semibold text-accent">
                {auth.user.value?.restaurant.currency || "CHF"}{" "}
                {baseAmount.value.toFixed(2)}
              </div>
            </div>
          </div>

          <div class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {recommendedTips.value.steps.map((step) => {
              const { type } = recommendedTips.value;
              const tipAmount =
                type === "percentage"
                  ? Number(((baseAmount.value * step) / 100).toFixed(2))
                  : Number(step.toFixed(2));
              const isSelected =
                customTipAmount.value == null &&
                Math.abs(selectedTipAmount.value - tipAmount) < 0.001;

              return (
                <button
                  key={step}
                  type="button"
                  onClick={() => handleQuickTip(tipAmount)}
                  class={`btn h-24 flex-col gap-1 ${isSelected ? "btn-primary" : "btn-accent"}`}
                  disabled={isSubmitting.value}
                >
                  <span class="text-xl font-semibold">
                    {type === "percentage"
                      ? `${step}%`
                      : `${auth.user.value?.restaurant.currency || "CHF"} ${step.toFixed(2)}`}
                  </span>
                  <span class="text-sm">
                    {auth.user.value?.restaurant.currency || "CHF"}{" "}
                    {tipAmount.toFixed(2)}
                  </span>
                </button>
              );
            })}

            <div class="h-24">
              <FastNumberInput
                class="btn btn-accent h-24 flex-col gap-1"
                value={customTipAmount.value}
                onChange={handleCustomTipChange}
                placeholder="Custom tip"
                min={0}
                showInModal={true}
                disabled={isSubmitting.value}
                simplified={true}
                simplifiedLabel="Custom"
                simplifiedHelperText={t("checkout.tips.custom_helper")}
              />
            </div>
          </div>

          <button
            type="button"
            class="btn btn-secondary mt-4 min-h-14 w-full text-lg"
            onClick={() => void handleConfirm(0)}
            disabled={isSubmitting.value}
          >
            {isSubmitting.value ? "Starting Payment..." : "Skip Tip"}
          </button>
        </div>
      );

      return () =>
        !props.isOpen ? null : isMobile.value ? (
          <AnimatedMobileTipsScreen
            isOpen={props.isOpen}
            baseAmount={baseAmount.value}
            selectedTipAmount={selectedTipAmount.value}
            customTipAmount={customTipAmount.value}
            isSubmitting={isSubmitting.value}
            recommendedTips={recommendedTips.value}
            onClose={props.onClose}
            onQuickTip={handleQuickTip}
            onCustomTipChange={handleCustomTipChange}
            onSkip={() => void handleConfirm(0)}
          />
        ) : (
          <LemmonModal
            title={"Tip"}
            class="h-full"
            onClose={props.onClose}
            disableBottomSheet={true}
          >
            {renderContent()}
          </LemmonModal>
        );
    },
  });
